import {
  getConfigForImportedTest,
  getConfigForMeasuredTest,
  getConfigForMeasuredTests,
} from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  wrapAsDerivedFromMeasurementsConfig as derivedFrom,
  measuredUsingConfigFor as measuredUsing,
  mergeConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { getSeparationPath } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { getDerivedFromMeasurementsConfigForPortionSieving } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/derivedFromMeasurementsConfig';
import { getMeasuredUsingConfigForPortionSieving } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/measuredUsingConfig';
import {
  DryMassSource,
  Phase,
  StateOfMaterial,
  WetMassSource,
} from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import qestIds from 'Components/Worksheets/QestIds';
import {
  IQuantity,
  isGreaterThanOrEqualTo as isIQuantityGreaterThanOrEqualTo,
  isLessThan as isIQuantityLessThan,
  toIQuantity,
} from 'Data/ViewModels/Core/IQuantity';
import * as oPath from 'Lib/Object/Path/Immutable';
import getLabelConfig from './labelConfig';

/**
 * Most masses are formatted based on the readability of the corresponding balance.
 * However, some require more complicated logic to determine which balances apply.
 * For these masses, we're currently keeping it simple and displaying them to a fixed 0.1 g
 * (default readability).
 */
const massConfig = { uom: 'g', format: { nearest: 0.1 } };

const getConfig = (prefix: string) => {
  const dryMassBalanceReadability = getDryMassBalance(prefix);
  const maxSeparations = 3;
  let config = {
    ...getPortionMassesConfig(`${prefix}.WholePortion`, 'Portion', dryMassBalanceReadability),
    ...getPortionWashingConfig(`${prefix}.WholePortion.Washing`),

    ...getPortionSievingConfig(prefix, `${prefix}.WholePortion`),
    ...getPortionWashingConfig(`${prefix}.WholePortion.Separation.FinePortion.Subspecimen.Washing`),
    [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: {
      options: [
        { value: false, label: 'Based on the selected sieve set' },
        { value: true, label: `Based on the sample's specification` },
      ],
    },
    [`${prefix}.Results.D85`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D60`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D50`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D30`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D15`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D10`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.CoefficientOfCurvature`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.CoefficientOfUniformity`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.FinenessModulus`]: { format: { nearest: 0.01 } },
    [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
    [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
  };
  Object.assign(
    config,
    ...[...Array(maxSeparations).keys()].map((i) => ({
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.CoarsePortion`,
        'Coarse Portion',
        dryMassBalanceReadability
      ),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.CoarsePortion`),
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.FinePortion`,
        'Fine Portion',
        dryMassBalanceReadability
      ),
      //For this test method, the "fine portion moisture content" is attached to the fine portion subspecimen,
      //but it is actually a *different* subspecimen. We avoid the 'Subspecimen' prefix because we don't want
      //users to be confused by it.
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen`,
        'Material',
        dryMassBalanceReadability
      ),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen`),
      ...getPortionWashingConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Washing`),
    }))
  );
  return mergeConfig(config, getLabelConfig(prefix));
};

const getPortionMassesConfig = (prefix: string, materialLabel: string, dryMassBalanceReadability: any) => {
  const massPrefix = `${prefix}.Masses`;

  return {
    [`${massPrefix}.WetMass`]: massConfig,
    [`${massPrefix}.DryMass`]: dryMassBalanceReadability,
    [`${massPrefix}.MassUpscaleFactor`]: { format: { nearest: 0.01 } },

    ...getPortionMassMeasurementConfig(massPrefix, materialLabel),
    ...getConfigForMeasuredTests(
      [qestIds.moistureContent.AS_1289_2_1_1.rev2005, qestIds.moistureContent.AS_1289_2_1_6.rev2005],
      `${massPrefix}.AssociatedMoistureContent`
    ),
    ...getConfigForImportedTest(
      qestIds.moistureContent.AS_1289_2_1_1.rev2005,
      `${massPrefix}.AssociatedMoistureContent`,
      [qestIds.moistureContent.AS_1289_2_1_6.rev2005]
    ),
    ...getPortionMeasuredConstantDryMassConfig(`${massPrefix}.MassMeasurement`),
    ...getMoistureContentPercent(`${massPrefix}.MoistureContent`),
    ...getMoistureContentPercent(`${massPrefix}.AssociatedMoistureContent.ExternalTest.Results.MoistureContentPercent`),
  };
};

const getMoistureContentPercent = (name: string) => ({
  [`${name}`]: { format: { nearest: 0.1 } },
});

const getPortionWashingConfig = (prefix: string) => {
  const measuredUsingDryMassBalance = measuredUsing(
    `${prefix}.AssociatedWashTest.MeasuredTest.Masses.MeasuredWashedConstantDryMass.Equipment.DryMassBalance`
  );

  return mergeConfig({
    [`${prefix}.WashedDryMass`]: measuredUsingDryMassBalance,
    ...getConfigForMeasuredTest(qestIds.wash.AS_1289_3_6_1.Rev2009, `${prefix}.AssociatedWashTest`),
  });
};

const getDryMassBalance = (prefix: string) => (configArgs: any) => {
  const dryingPhase = oPath.get(configArgs.worksheet, `${prefix}.Preparation.DryingPhase`) as Phase;
  const totalDryMassDeterminationBy = oPath.get(
    configArgs.worksheet,
    `${prefix}.Preparation.TotalDryMassDeterminationBy`
  ) as StateOfMaterial;

  if (dryingPhase === Phase.WholePortion && totalDryMassDeterminationBy === StateOfMaterial.OvenDry) {
    return measuredUsing(
      `${prefix}.WholePortion.Masses.MassMeasurement.MeasuredConstantDryMass.Equipment.DryMassBalance`
    );
  } else if (dryingPhase !== null) {
    return measuredUsing(`${prefix}.WholePortion.Masses.MassMeasurement.Equipment.DryMassBalance`);
  }

  return massConfig;
};

const getSieveConfig = (portionSievingPath: string) => (configArgs: any) => {
  const units = oPath.get(configArgs.worksheet, `${portionSievingPath}.Equipment.SieveSetUnits`);
  return {
    format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
    uom: units === 'Metric' ? 'mm' : 'in',
  };
};

//Note that the prefix parameter should already contain the root prefix within, we just needed the root separately
const getPortionSievingConfig = (rootPrefix: string, portionPath: string) => {
  const portionSievingPath = `${portionPath}.Sieving`;
  return mergeConfig(
    {
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Width`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Length`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Diameter`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Records.MaximumIndividualMassRetained`]: massConfig,
      [`${portionSievingPath}.Records.PercentagePassing`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.Records.PercentageRetained`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.Records.IndividualPercentageRetained`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.ExpectedIndividualPanMassRetained`]: massConfig,
      [`${portionSievingPath}.UpscaledPanMass`]: massConfig,
    },
    getMeasuredUsingConfigForPortionSieving(portionSievingPath),
    getDerivedFromMeasurementsConfigForPortionSieving(portionSievingPath),
    {
      [`${portionSievingPath}.Equipment.Balance`]: (configArgs: IConfigFunctionArgs) =>
        getDefaultReadability(oPath.get(configArgs.worksheet, `${portionPath}.Masses.DryMass`) as IQuantity),
    }
  );
};

const getPortionMassMeasurementConfig = (portionPrefix: string, materialLabel: string) => {
  const prefix = `${portionPrefix}.MassMeasurement`;
  const dryMassOnly = (configArgs: IConfigFunctionArgs) => {
    const wetMassSource = oPath.get(configArgs.worksheet, `${portionPrefix}.WetMassSource`) as WetMassSource;
    const dryMassSource = oPath.get(configArgs.worksheet, `${portionPrefix}.DryMassSource`) as DryMassSource;

    return wetMassSource !== WetMassSource.Measured && dryMassSource === DryMassSource.Measured;
  };

  const measuredUsingBalance = measuredUsing(`${prefix}.Equipment.Balance`);
  const measuredUsingDryMassBalance = measuredUsing(`${prefix}.Equipment.DryMassBalance`);

  return {
    [`${prefix}.TareID`]: { maxLength: 20 },
    [`${prefix}.TareMass`]: (configArgs: IConfigFunctionArgs) =>
      dryMassOnly(configArgs) ? measuredUsingDryMassBalance : measuredUsingBalance,
    [`${prefix}.TareAndWetMass`]: measuredUsingBalance,
    [`${prefix}.TareAndDryMass`]: measuredUsingDryMassBalance,

    [`${prefix}.WetMass`]: derivedFrom([`${prefix}.TareMass`, `${prefix}.TareAndWetMass`]),
    [`${prefix}.DryMass`]: derivedFrom([`${prefix}.TareMass`, `${prefix}.TareAndDryMass`]),

    [`${prefix}.Equipment.Balance`]: (configArgs: IConfigFunctionArgs) =>
      getDefaultReadability(oPath.get(configArgs.worksheet, `${prefix}.WetMass`) as IQuantity),
    [`${prefix}.Equipment.DryMassBalance`]: (configArgs: IConfigFunctionArgs) =>
      getDefaultReadability(oPath.get(configArgs.worksheet, `${prefix}.DryMass`) as IQuantity),
  };
};

const getPortionMeasuredConstantDryMassConfig = (massMeasurementPrefix: string) => {
  var prefix = `${massMeasurementPrefix}.MeasuredConstantDryMass`;
  const measuredUsingDryMassBalance = measuredUsing(`${prefix}.Equipment.DryMassBalance`);

  return {
    [`${prefix}.DryingTray.DryMassReadings.DrySoilAndContainerMass`]: measuredUsingDryMassBalance,
    [`${prefix}.DryingTray.DryMassReadings.DryingTime`]: {
      uom: 'h',
      format: { nearest: 0.01 },
    },
    [`${prefix}.Measurements.TotalDryMass`]: derivedFrom([
      `${massMeasurementPrefix}.TareMass`,
      `${prefix}.DryingTray.DryMassReadings.DrySoilAndContainerMass`,
    ]),
    [`${prefix}.Measurements.TotalDryMassToTotalWetMassRatio`]: {
      format: { nearest: 0.01 },
    },

    [`${prefix}.Equipment.DryingTemperature`]: { uom: '°C', format: { nearest: 1 } },
    [`${prefix}.Equipment.DryMassBalance`]: (configArgs: IConfigFunctionArgs) =>
      getDefaultReadability(oPath.get(configArgs.worksheet, `${prefix}.DryingTray.WetSoilMass`) as IQuantity),
  };
};

const getDefaultReadability = (sampleMass: IQuantity) => {
  //default readability: 0.1 when sampleMass >= 100 and sampleMass < 500
  let defaultReadability = 0.1;

  if (isIQuantityLessThan(sampleMass, toIQuantity(100, 'g'))) {
    defaultReadability = 0.01;
  } else if (isIQuantityGreaterThanOrEqualTo(sampleMass, toIQuantity(500, 'g'))) {
    defaultReadability = 1;
  }

  return {
    defaultReadability,
    defaultUom: 'g',
  };
};

export { getConfig };

export default {
  [qestIds.psdSieveAnalysis.AS_1289_3_6_1.Rev2009]: getConfig,
};
